import { useCallback, useMemo } from "react"

import ShippingOrderAssetExtended from "models/ShippingOrderAssetExtended"
import ReservationAsset from "models/ReservationAsset"
import ShippingOrderCartAsset from "models/ShippingOrderCartAsset"
import useAssetDisplayStatuses from "hooks/useAssetDisplayStatuses"
import { orderBy, uniq } from "lodash"
import RequestLocation from "models/RequestLocation"
import { IShippingOrderAssetsSort, ShippingOrderAssetsSortBy } from "../config"
import ShippingOrderAsset from "models/ShippingOrderAsset"
import { Guid } from "@/types"
import useAssets, { IUseAssetsParams } from "hooks/useAssets"
import ViewSettingsInventory from "models/ViewSettingsInventory"
import { FilterCategory } from "config/constants"
import { Dayjs } from "utils/dayjs"

interface IUseShippingOrderAssetsExtendedParams {
    reservationAssets: ReservationAsset[]
    assets: (ShippingOrderAsset | ShippingOrderCartAsset)[]
    selectAssetsInProgress?: boolean
    locations: RequestLocation[] | null
    reservationStartDate?: Dayjs
    reservationEndDate?: Dayjs
    sort: IShippingOrderAssetsSort | null
}

const emptyArray = []

export default function useShippingOrderAssetsExtended({
    reservationAssets,
    assets,
    locations,
    reservationStartDate,
    reservationEndDate,
    sort,
    selectAssetsInProgress,
}: IUseShippingOrderAssetsExtendedParams) {

    const assetIds = useMemo(() => uniq(
        assets
            .map(a => [a.assetId, "plannedAssetId" in a ? a.plannedAssetId : null])
            .flat()
            .filter(assetId => !!assetId) as Guid[]
    ), [assets])

    const shippingOrderAssetIds = useMemo(() => uniq(
        assets.map(asset => asset.assetId).filter((assetId): assetId is Guid => !!assetId)
    ), [assets])

    const {
        displayStatuses,
        loading: isLoadingAssetDisplayStatuses,
    } = useAssetDisplayStatuses(selectAssetsInProgress ? emptyArray : assetIds)

    const plannedAssetIds = useMemo(() => {
        return assets
            .map(a => "plannedAssetId" in a ? a.plannedAssetId : null)
            .filter(assetId => !!assetId) as Guid[]
    }, [assets])

    const missingReservationAssetIds = useMemo(() => {
        if (!reservationAssets.length) return shippingOrderAssetIds

        return shippingOrderAssetIds.filter(assetId => {
            const reservationAsset = reservationAssets.find(asset => asset.assetId === assetId)
            if (!reservationAsset) return true
            const hasCoreData = !!reservationAsset.articleId && !!reservationAsset.articleDescription && !!reservationAsset.lmid
            return !reservationAsset.isHydrated || !hasCoreData
        })
    }, [shippingOrderAssetIds, reservationAssets])

    const missingReservationAssetsParams: IUseAssetsParams = useMemo(() => ({
        viewSettings: new ViewSettingsInventory({ showInactiveAssets: true }),
        pageSize: missingReservationAssetIds.length,
        filters: missingReservationAssetIds.length ? missingReservationAssetIds.map(id => ({ category: FilterCategory.asset, value: id })) : null,
    }), [missingReservationAssetIds])

    const {
        assets: missingReservationAssets,
    } = useAssets(missingReservationAssetsParams)

    const plannedAssetParams: IUseAssetsParams = useMemo(() => ({
        viewSettings: new ViewSettingsInventory({ showInactiveAssets: true }),
        pageSize: plannedAssetIds.length,
        filters: plannedAssetIds.length ? plannedAssetIds.map(id => ({ category: FilterCategory.asset, value: id })) : null
    }), [plannedAssetIds])

    const {
        assets: plannedAssets,
    } = useAssets(plannedAssetParams)

    const sortAssets = useCallback((assetsExtended: ShippingOrderAssetExtended[]) => {
        if (!sort) return assetsExtended
        switch (sort.sortBy) {
            case ShippingOrderAssetsSortBy.articleDescription:
                return orderBy(assetsExtended, asset => [asset.articleDescription, asset.lmid], sort.sortDirection)
            case ShippingOrderAssetsSortBy.lmid:
                return orderBy(assetsExtended, asset => asset.lmid, sort.sortDirection)
            case ShippingOrderAssetsSortBy.stateOfStorage:
                return orderBy(assetsExtended, asset => [displayStatuses?.[asset.assetId]?.status, displayStatuses?.[asset.assetId]?.locationName, asset.lmid], sort.sortDirection)
            case ShippingOrderAssetsSortBy.fromLocation:
                return orderBy(assetsExtended, asset => [asset.departureLocation?.description, asset.lmid], sort.sortDirection)
            case ShippingOrderAssetsSortBy.toLocation:
                return orderBy(assetsExtended, asset => [asset.arrivalLocation?.description, asset.lmid], sort.sortDirection)
            case ShippingOrderAssetsSortBy.startDate:
                return orderBy(assetsExtended, asset => [asset.startDate?.valueOf(), asset.lmid], sort.sortDirection)
            case ShippingOrderAssetsSortBy.endDate:
                return orderBy(assetsExtended, asset => [asset.endDate?.valueOf(), asset.lmid], sort.sortDirection)
            default:
                return assetsExtended
        }
    }, [displayStatuses, sort])

    const shippingOrderAssetsExtended = useMemo(() => {
        const result: ShippingOrderAssetExtended[] = []
        return assets.reduce((aggregate, soAsset) => {
            const reservationAsset = reservationAssets.find(a => a.assetId === soAsset.assetId)
            const missingReservationAsset = missingReservationAssets?.find(asset => asset.assetId === soAsset.assetId)
            if (!reservationAsset && !missingReservationAsset) return aggregate

            const articleId = reservationAsset?.articleId || missingReservationAsset?.articleId || ""
            const articleDescription = reservationAsset?.articleDescription || missingReservationAsset?.articleDescription || ""
            const lmid = reservationAsset?.lmid || missingReservationAsset?.lmid || ""

            aggregate.push(new ShippingOrderAssetExtended({
                assetId: soAsset.assetId,
                lmid,
                articleThumbnailUri: reservationAsset?.articleThumbnailUri || missingReservationAsset?.thumbnailUri || undefined,
                articleDescription,
                altArticleDescription: reservationAsset?.altArticleDescription || missingReservationAsset?.altArticleDescription || undefined,
                articleId,
                startDate: reservationAsset?.start || reservationStartDate,
                endDate: reservationAsset?.end || reservationEndDate,
                isLabeled: reservationAsset?.isLabeled || missingReservationAsset?.isLabeled,
                homebaseId: reservationAsset?.homebaseId || missingReservationAsset?.homebaseId || undefined,
                departureLocationId: soAsset.departureLocationId,
                arrivalLocationId: soAsset.arrivalLocationId,
                displayStatus: displayStatuses?.[soAsset.assetId],
                releaseDate: "releaseDate" in soAsset ? soAsset.releaseDate : undefined,
                storeDate: "storeDate" in soAsset ? soAsset.storeDate : undefined,
                plannedAsset: "plannedAssetId" in soAsset && soAsset.plannedAssetId && plannedAssets
                    ? plannedAssets?.find(pa => pa.assetId === soAsset.plannedAssetId)
                    : undefined,
                plannedAssetDisplayStatus: "plannedAssetId" in soAsset && soAsset.plannedAssetId
                    ? displayStatuses?.[soAsset.plannedAssetId]
                    : undefined,
            }, locations))
            return sortAssets(aggregate)
        }, result)
    }, [reservationAssets, assets, missingReservationAssets, plannedAssets, reservationStartDate, reservationEndDate, displayStatuses, locations, sortAssets])

    return {
        shippingOrderAssetsExtended,
        isLoadingAssetDisplayStatuses,
    }
}
